import type {
	OpenOptions,
	TriggerSurveyOptions,
	WeloopApi,
	WeloopCallable,
	WeloopEvent,
	WeloopEventHandler,
} from "./types";

import { dispatch } from "./queue";

export interface ApiDependencies {
	readonly close: () => void;
	readonly hideLauncher: () => void;
	readonly identify: (token: string) => void;
	// A listener was added for `event`. Lets the survey embed deliver a survey
	// that arrived before the page subscribed.
	readonly onSubscribe?: (event: WeloopEvent) => void;
	readonly open: (options: OpenOptions | undefined) => void;
	readonly showLauncher: () => void;
	readonly triggerSurvey: (eventKey: string) => void;
}

export interface ApiHandle {
	readonly api: WeloopApi;
	/** Calls every listener of `event`; returns how many were called. */
	readonly emit: (event: WeloopEvent, payload?: unknown) => number;
}

export const createApi = (deps: ApiDependencies): ApiHandle => {
	const listeners = new Map<WeloopEvent, Set<WeloopEventHandler>>();

	const impl: WeloopApi = {
		close: () => {
			deps.close();
		},
		hideLauncher: () => {
			deps.hideLauncher();
		},
		identify: ({ token }) => {
			deps.identify(token);
		},
		off: (event, handler) => {
			listeners.get(event)?.delete(handler);
		},
		on: (event, handler) => {
			let set = listeners.get(event);
			if (set === undefined) {
				set = new Set();
				listeners.set(event, set);
			}
			set.add(handler);
			deps.onSubscribe?.(event);
		},
		open: (options) => {
			deps.open(options);
		},
		showLauncher: () => {
			deps.showLauncher();
		},
		// A mount cannot switch itself: `weloop.ts` owns switching, because it
		// has to dispose this mount and boot the next one.
		switchProject: () => {
			// noop
		},
		triggerSurvey: (options?: TriggerSurveyOptions) => {
			const event = options?.event;
			// Every prompt is raised by name: without the trigger ID there is
			// nothing to fire, and saying so beats a silent no-op.
			if (event === undefined || event === "") {
				console.warn(
					'[weloop] triggerSurvey needs { event: "<trigger ID>" } – copy it from the survey\'s setup step in the dashboard',
				);
				return;
			}
			deps.triggerSurvey(event);
		},
	};
	const api: WeloopApi = Object.freeze(impl);

	const emit = (event: WeloopEvent, payload?: unknown): number => {
		const set = listeners.get(event);
		if (set === undefined) {
			return 0;
		}
		for (const handler of set) {
			try {
				handler(payload);
			} catch (error) {
				console.error("[weloop] event handler threw:", error);
			}
		}
		return set.size;
	};

	return { api, emit };
};

// Bundles the API into a callable so existing `weloop("open")` call sites keep
// working after the stub is replaced. Methods are also exposed directly so
// `weloop.open()` works – through `dispatch` as well, so both forms validate
// the same way and neither can pass a deep link's fields.
export const toCallable = (api: WeloopApi): WeloopCallable => {
	const fn = (...args: readonly [string, ...unknown[]]): void => {
		if (args.length === 0 || typeof args[0] !== "string") {
			return;
		}
		dispatch(api, args);
	};
	const methods = Object.fromEntries(
		Object.keys(api).map((method) => [
			method,
			(...rest: readonly unknown[]) => {
				dispatch(api, [method, ...rest]);
			},
		]),
	);
	// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- the entries are built from the keys of `api`, one per method
	return Object.assign(fn, methods as unknown as WeloopApi, {
		loaded: true as const,
	});
};
